import React, { useState } from 'react';
import s from './tabs.module.css';
import t from '../../SimpleTable.module.css';
import Button from '../../Button';
import ConfirmDialog from '../../ConfirmDialog';
import {
  Menu, MenuForm, MenuItem, MenuSeparator,
} from '../../Menu';
import { composeWords, historyWords } from '../../../domain/notifications';
import { composeWord } from '../../../domain/compose';
import { useHistoryView } from '../../../hooks/useMessageHistory';
import { useLocale } from '../../../context/LocaleContext';
import Unread from '../../Unread';

// What went to this student's family, and what did not. The rows and their
// selection are `useMessageHistory` (src/hooks/useMessageHistory.js, the rule
// in core/src/messages.rs); this is the table.
//
// MMS's layout (#522, #546): the heading is "Messages from the [Last 3 Months
// ▾]", the period menu's entries the core's (`historyPeriods`). Under it,
// "+ New Message ▾" on the left offering Email Student, Email <each family
// contact> and Email All (`composeChoices`), and Sort ▾ and Search ▾ on the
// right (`useHistoryView`). The columns are MMS's Subject, Type, Date & Time,
// To, From and Status, then a ⋮ per row whose one entry is Forward — offered
// on every message whose words are kept: a teacher's own, and any other kind
// the sender kept the plain text of (#595, `can_forward` in the core). From is
// who the message went out as, recorded by the sender (#546), and blank where
// it was not recorded. A message sent later (#599) waits in Scheduled Queue
// under when it goes, as "Scheduled".
//
// Cancel Scheduled Send (#606): a message sent later that is still waiting
// offers it on its ⋮ (`canWithdraw`), and it asks first, because a withdrawn
// message is deleted. The words are ours (core/src/messages.rs says why).
//
// The files a message carried (#605) are listed by name under its subject,
// "Attached: …" — ours. The names outlive the bytes (the delivery log keeps
// them), so a row can list a file the sweep has since let go.
//
// A failed message carries its sentence on a line of its own under the row —
// "Not delivered to parent@example.com. The receiving mail server refused it
// …" — because a status cell alone would say that and not why. Beside it,
// Send again (#489) when the row offers it, saying who it will reach when the
// provider has reported some recipients delivered (#523): the message goes
// back to "Waiting to send" at once, and the sender picks it up.

// The When column in the studio's Region (#550), as every other date on the
// student's page (#534) and both phones' tabs: a German studio reads
// `30.09.2026, 16:00` on an American laptop. The zone is the studio's too; the
// browser's own locale decides nothing here.
function formatAt(iso, timeZone, locale, justNow) {
  if (!iso) return justNow;
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium', timeStyle: 'short', ...(timeZone ? { timeZone } : {}),
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

// `read`: the outbox, and the delivery log when the period asks for it, have
// answered (#629). Until then an empty list says nothing rather than "No
// messages in this period."
export default function MessageHistoryTab({
  rows,
  read = true,
  // The outbox stopped before it answered (#647).
  failed = false,
  timeZone = null,
  onSendAgain = null,
  // The period (#522). Without `periods` the heading is not drawn — the rows
  // are whatever the caller handed in.
  period = null,
  periods = null,
  onPeriodChange = null,
  // New Message ▾ (#522): its entries, and what choosing one does. Without
  // `onCompose` there is no button — a studio that cannot write sees none, as
  // it sees no Send again.
  choices = [],
  onCompose = null,
  // A row's Forward (#546), handed the row.
  onForward = null,
  // A row's Cancel Scheduled Send (#606), handed the message's id, once the
  // teacher has confirmed. Without it and Forward, no row has a ⋮.
  onWithdraw = null,
}) {
  const words = historyWords();
  const { locale } = useLocale();
  const compose = composeWords();
  // Which of the toolbar's menus is open, or which row's ⋮ (`row:<id>`).
  const [open, setOpen] = useState(null);
  const toggle = (which) => setOpen((cur) => (cur === which ? null : which));
  const close = () => setOpen(null);
  const view = useHistoryView(rows);
  const current = (periods || []).find((p) => p.code === period) || null;
  const empty = current?.emptyCode === 'historyEmptyScheduled' ? words.emptyScheduled : words.empty;
  const arrow = (key) => (view.sort.key === key ? (view.sort.descending ? ' ↓' : ' ↑') : '');
  const menus = Boolean(onForward || onWithdraw);
  const columns = ['Subject', 'Type', 'Date & Time', 'To', words.from, 'Status', ...(menus ? [''] : [])];
  // The row Cancel Scheduled Send is asking about, or null.
  const [withdrawing, setWithdrawing] = useState(null);

  return (
    <>
      {periods ? (
        <div className={s.sectionHead}>
          <label className={s.sectionTitle}>
            {words.heading}{' '}
            <select
              className={s.periodPick}
              value={period || ''}
              onChange={(e) => onPeriodChange?.(e.target.value)}
            >
              {periods.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}
            </select>
          </label>
        </div>
      ) : null}
      <div className={s.toolbar}>
        {onCompose ? (
          choices.length ? (
            <Menu
              open={open === 'compose'}
              onClose={close}
              align="left"
              trigger={(
                <Button variant="secondary" onClick={() => toggle('compose')} aria-haspopup="menu">
                  {`+ ${compose.newMessage} ▾`}
                </Button>
              )}
            >
              {choices.map((c) => (
                <MenuItem key={c.key} onClick={() => { close(); onCompose(c); }}>{c.label}</MenuItem>
              ))}
            </Menu>
          ) : <span className={s.muted}>{compose.nobody}</span>
        ) : null}
        <span className={s.spacer} />
        <Menu
          open={open === 'sort'}
          onClose={close}
          trigger={<Button variant="secondary" onClick={() => toggle('sort')}>{`${words.sort} ▾`}</Button>}
        >
          <MenuItem accent onClick={() => { view.clearSort(); close(); }}>{words.clearSort}</MenuItem>
          <MenuSeparator />
          {view.sortKeys.map((k) => (
            <MenuItem key={k.key} onClick={() => { view.sortBy(k.key); close(); }}>
              {k.label}{arrow(k.key)}
            </MenuItem>
          ))}
        </Menu>
        <MenuForm
          open={open === 'search'}
          onClose={close}
          trigger={<Button variant="secondary" onClick={() => toggle('search')}>{`${words.search} ▾`}</Button>}
        >
          <input
            className={s.searchField}
            placeholder={words.searchPlaceholder}
            aria-label={words.search}
            value={view.draft.query}
            onChange={(e) => view.setDraft({ ...view.draft, query: e.target.value })}
          />
          <label className={s.searchLabel}>
            {words.byType}
            <select
              className={s.searchField}
              value={view.draft.byType}
              onChange={(e) => view.setDraft({ ...view.draft, byType: e.target.value })}
            >
              {view.types.map((ty) => <option key={ty.code} value={ty.code}>{ty.label}</option>)}
            </select>
          </label>
          <label className={s.searchCheck}>
            <input
              type="checkbox"
              checked={view.draft.withinBody}
              onChange={(e) => view.setDraft({ ...view.draft, withinBody: e.target.checked })}
            />
            {words.searchBody}
          </label>
          <div className={s.searchRule} />
          <div className={s.searchActions}>
            <Button variant="secondary" onClick={() => { view.clearSearch(); close(); }}>{words.clear}</Button>
            <Button onClick={() => { view.applySearch(); close(); }}>{words.search}</Button>
          </div>
        </MenuForm>
      </div>
      <p className={s.hint}>{words.hint}</p>
      {/* Laid out to fit the student page's column at 1280 wide (#644):
          tabs.module.css has why, beside `.historyScroll`. */}
      <div className={t.wrap}>
        <div className={s.historyScroll}>
          <table className={t.table}>
            <thead><tr>{columns.map((c, i) => <th key={c || i}>{c}</th>)}</tr></thead>
            <tbody>
              {view.rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr>
                    <td className={s.historySubject}>
                      {r.subject ?? r.kind}
                      {r.attachments?.length ? (
                        <div className={t.muted}>{`${composeWord('attach_listed')}: ${r.attachments.join(', ')}`}</div>
                      ) : null}
                    </td>
                    <td>{r.channel}</td>
                    <td>{formatAt(r.at, timeZone, locale, words.justNow)}</td>
                    <td className={s.historyAddress}>{r.to.join(', ') || '—'}</td>
                    <td className={s.historyAddress}>{r.from || ''}</td>
                    <td className={r.failed ? `${s.broken} ${s.historyStatus}` : s.historyStatus}>{r.statusLabel}</td>
                    {menus ? (
                      <td>
                        {(onForward && r.canForward) || (onWithdraw && r.canWithdraw) ? (
                          <Menu
                            open={open === `row:${r.id}`}
                            onClose={close}
                            trigger={(
                              <button className={s.kebab} aria-label={words.rowMenu} onClick={() => toggle(`row:${r.id}`)}>
                                ⋮
                              </button>
                            )}
                          >
                            {onForward && r.canForward ? (
                              <MenuItem onClick={() => { close(); onForward(r); }}>{words.forward}</MenuItem>
                            ) : null}
                            {onWithdraw && r.canWithdraw ? (
                              <MenuItem onClick={() => { close(); setWithdrawing(r); }}>{words.withdraw}</MenuItem>
                            ) : null}
                          </Menu>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                  {r.failed && (r.error || (r.canSendAgain && onSendAgain)) ? (
                    <tr>
                      <td colSpan={columns.length} className={s.broken}>
                        {r.error}
                        {r.canSendAgain && onSendAgain ? (
                          <>
                            {r.error ? ' ' : null}
                            <button className={t.linkButton} onClick={() => onSendAgain(r.id)}>
                              {r.sendAgainLabel || words.sendAgain}
                            </button>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              ))}
              {view.rows.length === 0 ? (
                <tr><td colSpan={columns.length} className={t.muted}>
                  {read ? empty : <Unread as="span" failed={failed} />}
                </td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmDialog
        open={Boolean(withdrawing)}
        title={words.withdrawTitle}
        message={withdrawing ? `${withdrawing.subject ?? withdrawing.kind} — ${words.withdrawMessage}` : ''}
        discardLabel={words.withdrawKeep}
        cancelLabel={null}
        confirmLabel={words.withdrawConfirm}
        onDiscard={() => setWithdrawing(null)}
        onCancel={() => setWithdrawing(null)}
        onConfirm={() => {
          const id = withdrawing?.id;
          setWithdrawing(null);
          if (id) onWithdraw?.(id);
        }}
      />
    </>
  );
}
